<script setup lang="ts">
import { useCookie, useNuxtApp, useRoute } from '#imports'
import LazyGridOverlay from '@voix/components/slicer/design-guide/GridOverlay.vue'
import { getCurrentUser } from '@voix/composables/queries/useQueryCurrentUser'
import { useVoixUserAuth } from '@voix/composables/useVoixUserAuth'
import { usePageStore } from '@voix/store/pageStore'
import { computed, onMounted, provide, ref } from 'vue'

import VoixElementsLoop from './VoixElementsLoop.vue'

const pageStore = usePageStore()

function kebabCase(string: string) {
  return string
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

const originalLayoutName = computed(() => pageStore.currentPage?.layout)

const layout = computed(() => {
  if (pageStore.currentPage)
    return kebabCase(pageStore.currentPage.layout)

  return null
})

provide('voixLayout', layout)

const route = useRoute()
const showGrid = computed(() => route.query['show-grid'] === 'true')

// Top element is the first visible element on the page
// We use this to determine if we should apply special
// features to the media in that element.
const topElement = computed(() => {
  return pageStore.currentPage.elements.find(element => element.enabled === true)
})
provide('topElement', topElement.value)

// Emit an event to track the page view
const { $voix } = useNuxtApp()
onMounted(() => {
  $voix.eventBus.emit('voix:page:viewed', pageStore.currentPage)
})

// If the user is logged in we will get the current user
const accessToken = useCookie('auth-access-token')
if (accessToken.value)
  getCurrentUser()

const { checkUserPermissions } = useVoixUserAuth()
const canEdit = ref(false)
await checkUserPermissions(['Pages.Edit']).then((value) => {
  canEdit.value = value
})
</script>

<template>
  <div>
    <NuxtLayout v-if="layout && pageStore.currentPage" :name="layout">
      <VoixElementsLoop
        :elements="pageStore.currentPage.elements"
      />
    </NuxtLayout>

    <LazyVoixEditorShortcut v-if="canEdit" />
    <LazyGridOverlay v-if="showGrid" :layout="originalLayoutName" />
  </div>
</template>
